import type { GlobalThemeOverrides } from 'naive-ui'
import { computed, watch } from 'vue'
import { darkTheme, useOsTheme } from 'naive-ui'
import { darken, lighten } from 'polished'
import {
  componentThemeOverrides,
  generateDarkTheme,
  generateLightTheme,
} from '@/config/naiveui-theme'
import { useAppStore } from '@/store'

function updateThemeColors(color: string) {
  const root = document.documentElement
  root.style.setProperty('--color-primary', color)
  root.style.setProperty('--color-primary-light', lighten(0.1, color))
  root.style.setProperty('--color-primary-dark', darken(0.1, color))

  // Update CSS variables
  root.style.setProperty('--n-primary-color', color)
  root.style.setProperty('--n-primary-color-hover', lighten(0.1, color))
  root.style.setProperty('--n-primary-color-pressed', darken(0.1, color))
  root.style.setProperty('--n-primary-color-suppl', lighten(0.15, color))
}

export function useTheme() {
  const appStore = useAppStore()
  const OsTheme = useOsTheme()

  const isDark = computed(() => {
    if (appStore.theme === 'auto')
      return OsTheme.value === 'dark'
    else
      return appStore.theme === 'dark'
  })

  const colorTheme = computed(() => {
    if (appStore.colorTheme)
      return appStore.colorTheme
    return null
  })

  const theme = computed(() => {
    return isDark.value ? darkTheme : undefined
  })

  const themeOverrides = computed<GlobalThemeOverrides>(() => {
    // Use new theme generation system
    const baseTheme = isDark.value
      ? generateDarkTheme(colorTheme.value || undefined)
      : generateLightTheme(colorTheme.value || undefined)

    // Merge with component-specific overrides
    return {
      ...baseTheme,
      ...componentThemeOverrides,
    }
  })

  watch(
    () => isDark.value,
    (dark) => {
      if (dark)
        document.documentElement.classList.add('dark')
      else
        document.documentElement.classList.remove('dark')
    },
    { immediate: true },
  )

  watch(
    () => colorTheme.value,
    (color) => {
      if (color)
        updateThemeColors(color)
    },
    { immediate: true },
  )

  return { theme, themeOverrides, isDark }
}
